import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/fetchWithAuth";
import { 
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import NPSCallTracker from "@/components/NPSCallTracker";
import { useOrganization } from "@/contexts/OrganizationContext";

interface SurveyFormProps {
  onSubmit: (data: any) => void;
  isLoading: boolean;
}

type NPSCallFormData = {
  patientName: string;
  phoneNumber: string;
  locationId: string;
  lastVisitDate: string;
  callStatus: 'Pending' | 'Completed' | 'Declined' | 'Voicemail';
  score: number | null;
  feedback: string;
  wouldRecommend: boolean | null;
  followUpRequested: boolean;
  needsAssistance: boolean;
  requestedReview: boolean;
  giftCardSent: boolean;
  additionalComments: string;
  // New questions from the updated survey
  doctorCareRating: string | null;
  opticianCareRating: string | null;
  eyewearSatisfaction: string | null;
  glassesReceivedOnTime: boolean | null;
  contactsReceivedOnTime: boolean | null;
  appointmentEfficiency: string | null;
  officeCleanliness: string | null;
  feltWelcomed: boolean | null;
  wellInformed: boolean | null;
  improvementSuggestions: string;
  teamMemberToRecognize: string;
};

export default function NPSSurveyCall() {
  const [activeTab, setActiveTab] = useState("newCall");
  const [selectedCall, setSelectedCall] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();
  const { currentOrganizationId } = useOrganization();
  
  // Initial form state
  const initialFormState: NPSCallFormData = {
    patientName: "",
    phoneNumber: "",
    locationId: "no-selection",
    lastVisitDate: format(new Date(), 'yyyy-MM-dd'),
    callStatus: 'Pending',
    score: null,
    feedback: "",
    wouldRecommend: null,
    followUpRequested: false,
    needsAssistance: false,
    requestedReview: false,
    giftCardSent: false,
    additionalComments: "",
    // New fields
    doctorCareRating: null,
    opticianCareRating: null,
    eyewearSatisfaction: null,
    glassesReceivedOnTime: null,
    contactsReceivedOnTime: null,
    appointmentEfficiency: null,
    officeCleanliness: null,
    feltWelcomed: null,
    wellInformed: null,
    improvementSuggestions: "",
    teamMemberToRecognize: ""
  };
  
  const [formData, setFormData] = useState<NPSCallFormData>(initialFormState);
  
  // Fetch the list of locations
  const { data: locations, isLoading: isLoadingLocations } = useQuery<any[]>({
    queryKey: ['/api/locations', currentOrganizationId],
    queryFn: async () => {
      try {
        const orgParam = currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : '';
        const response = await apiRequest<any[]>(`/api/locations${orgParam}`);
        return response || [];
      } catch (error) {
        console.error("Failed to fetch locations:", error);
        return [];
      }
    }
  });

  // Fetch pending calls
  const { data: pendingCalls, isLoading: isLoadingCalls } = useQuery<any[]>({
    queryKey: ['/api/nps/patient-calls', currentOrganizationId],
    queryFn: async () => {
      try {
        const orgParam = currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : '';
        const response = await apiRequest<any[]>(`/api/nps/patient-calls${orgParam}`);
        return response || [];
      } catch (error) {
        console.error("Failed to fetch pending calls:", error);
        return [];
      }
    }
  });

  // Fetch completed calls (using the same data as pending calls)
  const { data: completedCalls } = useQuery<any[]>({
    queryKey: ['/api/nps/patient-calls-completed', currentOrganizationId],
    queryFn: async () => {
      try {
        const orgParam = currentOrganizationId ? `?organizationId=${encodeURIComponent(currentOrganizationId)}` : '';
        const response = await apiRequest<any[]>(`/api/nps/patient-calls${orgParam}`);
        return response || [];
      } catch (error) {
        console.error("Failed to fetch completed calls:", error);
        return [];
      }
    }
  });
  
  // Mutation for submitting the form
  const submitMutation = useMutation({
    mutationFn: async (formData: NPSCallFormData) => {
      return await apiRequest<any>('/api/nps/patient-calls', {
        method: 'POST',
        data: formData
      });
    },
    onSuccess: () => {
      toast({
        title: "Survey call saved",
        description: "The patient survey call has been recorded successfully",
      });
      // Reset form after successful submission
      setFormData(initialFormState);
      // Refresh the pending calls list
      queryClient.invalidateQueries({ queryKey: ['/api/nps/patient-calls'] });
    },
    onError: (error) => {
      console.error("Error submitting survey:", error);
      toast({
        title: "Error",
        description: "There was an error saving the survey. Please try again.",
        variant: "destructive",
      });
    }
  });
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMutation.mutate(formData);
  };
  
  // Handler for loading an existing patient call
  const handleLoadPatientCall = (call: any) => {
    setFormData({
      patientName: call.patientName,
      phoneNumber: call.phoneNumber,
      locationId: call.locationId,
      lastVisitDate: format(new Date(call.lastVisitDate), 'yyyy-MM-dd'),
      callStatus: call.callStatus,
      score: call.score || null,
      feedback: call.feedback || "",
      wouldRecommend: call.wouldRecommend || null,
      followUpRequested: call.followUpRequested || false,
      needsAssistance: call.needsAssistance || false,
      requestedReview: call.requestedReview || false,
      giftCardSent: call.giftCardSent || false,
      additionalComments: call.additionalComments || "",
      // New fields
      doctorCareRating: call.doctorCareRating || null,
      opticianCareRating: call.opticianCareRating || null,
      eyewearSatisfaction: call.eyewearSatisfaction || null,
      glassesReceivedOnTime: call.glassesReceivedOnTime || null,
      contactsReceivedOnTime: call.contactsReceivedOnTime || null,
      appointmentEfficiency: call.appointmentEfficiency || null,
      officeCleanliness: call.officeCleanliness || null,
      feltWelcomed: call.feltWelcomed || null,
      wellInformed: call.wellInformed || null,
      improvementSuggestions: call.improvementSuggestions || "",
      teamMemberToRecognize: call.teamMemberToRecognize || ""
    });
    setActiveTab("newCall");
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">NPS Survey Calls</h1>
      </div>

      <Tabs defaultValue="newCall" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="tracker">📊 Call Tracker</TabsTrigger>
          <TabsTrigger value="newCall">New Survey Call</TabsTrigger>
          <TabsTrigger value="pendingCalls">Pending Calls</TabsTrigger>
          <TabsTrigger value="completedCalls">Completed Calls</TabsTrigger>
        </TabsList>

        <TabsContent value="tracker">
          <NPSCallTracker />
        </TabsContent>

        <TabsContent value="newCall">
          <Card>
            <CardHeader>
              <CardTitle>Patient Survey Call</CardTitle>
              <CardDescription>
                Enter patient information and complete the NPS survey
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Patient Information Section */}
                  <Card className="col-span-2">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Patient Information</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="patientName">Patient Name</Label>
                        <Input 
                          id="patientName" 
                          name="patientName"
                          value={formData.patientName}
                          onChange={handleInputChange}
                          placeholder="Enter patient name" 
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phoneNumber">Phone Number</Label>
                        <Input 
                          id="phoneNumber" 
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          placeholder="Enter phone number" 
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="location">Location</Label>
                        <Select value={formData.locationId === "no-selection" ? undefined : formData.locationId} onValueChange={(value) => handleSelectChange("locationId", value)}>
                          <SelectTrigger id="location">
                            <SelectValue placeholder="Select location" />
                          </SelectTrigger>
                          <SelectContent>
                            {isLoadingLocations ? (
                              <SelectItem value="loading">Loading locations...</SelectItem>
                            ) : locations?.length ? (
                              locations.map((loc: any) => (
                                <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                              ))
                            ) : (
                              <SelectItem value="no-locations" disabled>No locations available</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastVisitDate">Last Visit Date</Label>
                        <Input 
                          id="lastVisitDate" 
                          name="lastVisitDate"
                          type="date" 
                          value={formData.lastVisitDate}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="callStatus">Call Status</Label>
                        <Select 
                          value={formData.callStatus} 
                          onValueChange={(value) => handleSelectChange("callStatus", value as 'Pending' | 'Completed' | 'Declined' | 'Voicemail')}
                        >
                          <SelectTrigger id="callStatus">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Completed">Completed</SelectItem>
                            <SelectItem value="Declined">Declined</SelectItem>
                            <SelectItem value="Voicemail">Voicemail</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Survey Questions */}
                  <Card className="col-span-2">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">NPS Survey Questions</CardTitle>
                      <CardDescription>
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button variant="outline">View Full Survey Script</Button>
                          </SheetTrigger>
                          <SheetContent className="w-[500px] sm:w-[540px] overflow-y-auto">
                            <SheetHeader>
                              <SheetTitle>NPS Survey Script</SheetTitle>
                              <SheetDescription>
                                The complete survey script to read to patients
                              </SheetDescription>
                            </SheetHeader>
                            <div className="py-4 space-y-4">
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Introduction</p>
                                <p className="text-sm text-muted-foreground">
                                  "Hello, may I please speak with [Patient Name]? Hi, this is [Your Name] calling from Bolt Eye Group. We're following up on your visit from [Date] at our [Location] clinic. We're conducting a brief survey to help improve our services, and as a thank you for your time, we'd like to offer you a $20 Amazon gift card. Would you have about 5 minutes to answer a few questions?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Survey Introduction</p>
                                <p className="text-sm text-muted-foreground">
                                  "Thank you for agreeing to participate. Your feedback is incredibly valuable to us. I'll be asking you a few questions about your experience at our clinic."
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">NPS Question</p>
                                <p className="text-sm text-muted-foreground">
                                  "On a scale from 0 to 10, with 0 being not at all likely and 10 being extremely likely, how likely are you to recommend Bolt Eye Group to a friend or family member?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Follow-up Question</p>
                                <p className="text-sm text-muted-foreground">
                                  "Can you please share what factors contributed to the score you gave us?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Doctor Care Rating</p>
                                <p className="text-sm text-muted-foreground">
                                  "How would you rate the care and attention provided by your doctor? Would you say it was Excellent, Good, Fair, or Poor?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Optician Care Rating</p>
                                <p className="text-sm text-muted-foreground">
                                  "How would you rate the care and attention provided by your optician—the person who helped you with glasses or contacts?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Eyewear Satisfaction</p>
                                <p className="text-sm text-muted-foreground">
                                  "Were you satisfied with the selection of eyewear and the help you received choosing frames or lenses?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Glasses Delivery</p>
                                <p className="text-sm text-muted-foreground">
                                  "Did you receive your glasses within 3 weeks of ordering?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Contacts Delivery</p>
                                <p className="text-sm text-muted-foreground">
                                  "Did your contact lenses arrive within 1 week of ordering?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Appointment Efficiency</p>
                                <p className="text-sm text-muted-foreground">
                                  "How would you rate the efficiency of your overall appointment—from check-in to checkout?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Office Cleanliness</p>
                                <p className="text-sm text-muted-foreground">
                                  "How would you rate the cleanliness and comfort of the office?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Felt Welcomed</p>
                                <p className="text-sm text-muted-foreground">
                                  "Did our team make you feel welcomed and cared for during your visit?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Well Informed</p>
                                <p className="text-sm text-muted-foreground">
                                  "Did you feel well-informed about your diagnosis, next steps, and any billing or insurance information?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Improvement Suggestions</p>
                                <p className="text-sm text-muted-foreground">
                                  "Was there anything we could have done better to improve your experience?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Team Member Recognition</p>
                                <p className="text-sm text-muted-foreground">
                                  "Is there a team member you'd like to personally recognize for great service?"
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Google Review Request (For scores 9-10)</p>
                                <p className="text-sm text-muted-foreground">
                                  "We're so glad to hear you had a positive experience! Would you be willing to share your experience in a Google review? It would help other patients find quality eye care. We can send you a link via text message that makes it easy to leave a review."
                                </p>
                              </div>
                              
                              <div className="border-l-4 border-purple-800 pl-4 py-2 bg-purple-50 rounded">
                                <p className="font-semibold">Conclusion</p>
                                <p className="text-sm text-muted-foreground">
                                  "Thank you so much for your time and feedback today. To show our appreciation, we'd like to send you a $20 Amazon gift card. Could you please confirm your email address so we can send it to you? We value your input and look forward to seeing you at your next appointment."
                                </p>
                              </div>
                            </div>
                            <SheetFooter>
                              <SheetClose asChild>
                                <Button>Close</Button>
                              </SheetClose>
                            </SheetFooter>
                          </SheetContent>
                        </Sheet>
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-4">
                        <Label>
                          1. On a scale from 0 to 10, how likely are you to recommend Bolt Eye Group to a friend or family member?
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                            <Button
                              key={score}
                              type="button"
                              variant={formData.score === score ? "default" : "outline"}
                              className={`w-12 h-12 ${
                                formData.score === score ? "bg-purple-800 text-white" : ""
                              }`}
                              onClick={() => setFormData({ ...formData, score: score })}
                            >
                              {score}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="feedback">
                          2. Can you please share what factors contributed to the score you gave us?
                        </Label>
                        <Textarea
                          id="feedback"
                          name="feedback"
                          value={formData.feedback}
                          onChange={handleInputChange}
                          placeholder="Enter patient feedback"
                          className="min-h-[100px]"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="doctorCareRating">
                          3. How would you rate the care and attention provided by your doctor?
                        </Label>
                        <RadioGroup 
                          value={formData.doctorCareRating || ""} 
                          onValueChange={(value) => handleSelectChange("doctorCareRating", value)}
                          className="flex flex-col space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Excellent" id="doctorExcellent" />
                            <Label htmlFor="doctorExcellent" className="font-normal">Excellent</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Good" id="doctorGood" />
                            <Label htmlFor="doctorGood" className="font-normal">Good</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Fair" id="doctorFair" />
                            <Label htmlFor="doctorFair" className="font-normal">Fair</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Poor" id="doctorPoor" />
                            <Label htmlFor="doctorPoor" className="font-normal">Poor</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="opticianCareRating">
                          4. How would you rate the care and attention provided by your optician?
                        </Label>
                        <RadioGroup 
                          value={formData.opticianCareRating || ""} 
                          onValueChange={(value) => handleSelectChange("opticianCareRating", value)}
                          className="flex flex-col space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Excellent" id="opticianExcellent" />
                            <Label htmlFor="opticianExcellent" className="font-normal">Excellent</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Good" id="opticianGood" />
                            <Label htmlFor="opticianGood" className="font-normal">Good</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Fair" id="opticianFair" />
                            <Label htmlFor="opticianFair" className="font-normal">Fair</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Poor" id="opticianPoor" />
                            <Label htmlFor="opticianPoor" className="font-normal">Poor</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Did not see optician" id="opticianNA" />
                            <Label htmlFor="opticianNA" className="font-normal">Did not see optician</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="eyewearSatisfaction">
                          5. Were you satisfied with the selection of eyewear and the help you received choosing frames or lenses?
                        </Label>
                        <RadioGroup 
                          value={formData.eyewearSatisfaction || ""} 
                          onValueChange={(value) => handleSelectChange("eyewearSatisfaction", value)}
                          className="flex flex-col space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Very satisfied" id="eyewearVerySatisfied" />
                            <Label htmlFor="eyewearVerySatisfied" className="font-normal">Very satisfied</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Satisfied" id="eyewearSatisfied" />
                            <Label htmlFor="eyewearSatisfied" className="font-normal">Satisfied</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Neutral" id="eyewearNeutral" />
                            <Label htmlFor="eyewearNeutral" className="font-normal">Neutral</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Dissatisfied" id="eyewearDissatisfied" />
                            <Label htmlFor="eyewearDissatisfied" className="font-normal">Dissatisfied</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Did not browse/purchase eyewear" id="eyewearNA" />
                            <Label htmlFor="eyewearNA" className="font-normal">Did not browse/purchase eyewear</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="glassesReceivedOnTime">
                          6. Did you receive your glasses within 3 weeks of ordering?
                        </Label>
                        <RadioGroup 
                          value={formData.glassesReceivedOnTime === null ? "" : formData.glassesReceivedOnTime ? "Yes" : "No"} 
                          onValueChange={(value) => handleCheckboxChange("glassesReceivedOnTime", value === "Yes")}
                          className="flex flex-col space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Yes" id="glassesYes" />
                            <Label htmlFor="glassesYes" className="font-normal">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="No" id="glassesNo" />
                            <Label htmlFor="glassesNo" className="font-normal">No</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="" id="glassesNA" />
                            <Label htmlFor="glassesNA" className="font-normal">Did not order glasses</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="contactsReceivedOnTime">
                          7. Did your contact lenses arrive within 1 week of ordering?
                        </Label>
                        <RadioGroup 
                          value={formData.contactsReceivedOnTime === null ? "" : formData.contactsReceivedOnTime ? "Yes" : "No"} 
                          onValueChange={(value) => handleCheckboxChange("contactsReceivedOnTime", value === "Yes")}
                          className="flex flex-col space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Yes" id="contactsYes" />
                            <Label htmlFor="contactsYes" className="font-normal">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="No" id="contactsNo" />
                            <Label htmlFor="contactsNo" className="font-normal">No</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="" id="contactsNA" />
                            <Label htmlFor="contactsNA" className="font-normal">Did not order contacts</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="appointmentEfficiency">
                          8. How would you rate the efficiency of your overall appointment—from check-in to checkout?
                        </Label>
                        <RadioGroup 
                          value={formData.appointmentEfficiency || ""} 
                          onValueChange={(value) => handleSelectChange("appointmentEfficiency", value)}
                          className="flex flex-col space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Excellent" id="efficiencyExcellent" />
                            <Label htmlFor="efficiencyExcellent" className="font-normal">Excellent</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Good" id="efficiencyGood" />
                            <Label htmlFor="efficiencyGood" className="font-normal">Good</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Fair" id="efficiencyFair" />
                            <Label htmlFor="efficiencyFair" className="font-normal">Fair</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Poor" id="efficiencyPoor" />
                            <Label htmlFor="efficiencyPoor" className="font-normal">Poor</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="officeCleanliness">
                          9. How would you rate the cleanliness and comfort of the office?
                        </Label>
                        <RadioGroup 
                          value={formData.officeCleanliness || ""} 
                          onValueChange={(value) => handleSelectChange("officeCleanliness", value)}
                          className="flex flex-col space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Excellent" id="cleanExcellent" />
                            <Label htmlFor="cleanExcellent" className="font-normal">Excellent</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Good" id="cleanGood" />
                            <Label htmlFor="cleanGood" className="font-normal">Good</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Fair" id="cleanFair" />
                            <Label htmlFor="cleanFair" className="font-normal">Fair</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Poor" id="cleanPoor" />
                            <Label htmlFor="cleanPoor" className="font-normal">Poor</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>
                          10. Did our team make you feel welcomed and cared for during your visit?
                        </Label>
                        <RadioGroup 
                          value={formData.feltWelcomed === null ? "" : formData.feltWelcomed ? "Yes" : "No"} 
                          onValueChange={(value) => handleCheckboxChange("feltWelcomed", value === "Yes")}
                          className="flex flex-col space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Yes" id="welcomedYes" />
                            <Label htmlFor="welcomedYes" className="font-normal">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="No" id="welcomedNo" />
                            <Label htmlFor="welcomedNo" className="font-normal">No</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>
                          11. Did you feel well-informed about your diagnosis, next steps, and any billing or insurance information?
                        </Label>
                        <RadioGroup 
                          value={formData.wellInformed === null ? "" : formData.wellInformed ? "Yes" : "No"} 
                          onValueChange={(value) => handleCheckboxChange("wellInformed", value === "Yes")}
                          className="flex flex-col space-y-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="Yes" id="informedYes" />
                            <Label htmlFor="informedYes" className="font-normal">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="No" id="informedNo" />
                            <Label htmlFor="informedNo" className="font-normal">No</Label>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="improvementSuggestions">
                          12. Was there anything we could have done better to improve your experience?
                        </Label>
                        <Textarea
                          id="improvementSuggestions"
                          name="improvementSuggestions"
                          value={formData.improvementSuggestions}
                          onChange={handleInputChange}
                          placeholder="Enter patient suggestions"
                          className="min-h-[100px]"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="teamMemberToRecognize">
                          13. Is there a team member you'd like to personally recognize for great service?
                        </Label>
                        <Textarea
                          id="teamMemberToRecognize"
                          name="teamMemberToRecognize"
                          value={formData.teamMemberToRecognize}
                          onChange={handleInputChange}
                          placeholder="Enter team member names"
                          className="min-h-[80px]"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="additionalComments">
                          14. Additional comments, questions, or concerns from the patient:
                        </Label>
                        <Textarea
                          id="additionalComments"
                          name="additionalComments"
                          value={formData.additionalComments}
                          onChange={handleInputChange}
                          placeholder="Enter any additional patient comments"
                          className="min-h-[100px]"
                        />
                      </div>
                      
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="followUpRequested" 
                            checked={formData.followUpRequested}
                            onCheckedChange={(checked) => handleCheckboxChange("followUpRequested", checked as boolean)}
                          />
                          <Label htmlFor="followUpRequested">Patient requested follow-up contact</Label>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="needsAssistance" 
                            checked={formData.needsAssistance}
                            onCheckedChange={(checked) => handleCheckboxChange("needsAssistance", checked as boolean)}
                          />
                          <Label htmlFor="needsAssistance">Patient needs assistance with eye care</Label>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="requestedReview" 
                            checked={formData.requestedReview}
                            onCheckedChange={(checked) => handleCheckboxChange("requestedReview", checked as boolean)}
                          />
                          <Label htmlFor="requestedReview">Patient agreed to write a Google review</Label>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="giftCardSent" 
                            checked={formData.giftCardSent}
                            onCheckedChange={(checked) => handleCheckboxChange("giftCardSent", checked as boolean)}
                          />
                          <Label htmlFor="giftCardSent">$20 Amazon gift card sent to patient</Label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="col-span-2 flex justify-end">
                    <Button 
                      type="submit" 
                      size="lg" 
                      disabled={submitMutation.isPending}
                      className="w-full md:w-auto"
                    >
                      {submitMutation.isPending ? "Saving..." : "Save Survey Call"}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pendingCalls">
          <Card>
            <CardHeader>
              <CardTitle>Pending Survey Calls</CardTitle>
              <CardDescription>
                Patients that need to be called for NPS surveys
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <table className="min-w-full divide-y divide-border">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Patient Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Phone Number</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Last Visit</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {isLoadingCalls ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 text-center">Loading pending calls...</td>
                      </tr>
                    ) : pendingCalls?.length ? (
                      pendingCalls
                        .filter((call: any) => call.callStatus === 'Pending')
                        .map((call: any) => (
                          <tr key={call.id}>
                            <td className="px-4 py-3 text-sm">{call.patientName}</td>
                            <td className="px-4 py-3 text-sm">{call.phoneNumber}</td>
                            <td className="px-4 py-3 text-sm">{call.locationName}</td>
                            <td className="px-4 py-3 text-sm">{new Date(call.lastVisitDate).toLocaleDateString()}</td>
                            <td className="px-4 py-3 text-sm">
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Pending</Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleLoadPatientCall(call)}
                              >
                                Call Now
                              </Button>
                            </td>
                          </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 text-center">
                          <div className="py-12">
                            <p className="text-center text-muted-foreground">No pending calls found</p>
                            <Button onClick={() => setActiveTab("newCall")} className="mt-4 mx-auto block">
                              Create New Call
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                    

                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completedCalls">
          <Card>
            <CardHeader>
              <CardTitle>Completed Survey Calls</CardTitle>
              <CardDescription>
                View completed NPS survey calls and responses
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <table className="min-w-full divide-y divide-border">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Patient Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Call Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">NPS Score</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Google Review</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Gift Card</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {isLoadingCalls ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 text-center">Loading completed calls...</td>
                      </tr>
                    ) : completedCalls?.length ? (
                      completedCalls
                        .filter((call: any) => call.callStatus === 'Completed')
                        .map((call: any) => (
                          <tr key={call.id}>
                            <td className="px-4 py-3 text-sm">{call.patientName}</td>
                            <td className="px-4 py-3 text-sm">{call.locationName}</td>
                            <td className="px-4 py-3 text-sm">{new Date(call.callDate || call.lastVisitDate).toLocaleDateString()}</td>
                            <td className="px-4 py-3 text-sm">
                              {call.score !== null ? (
                                <span className="text-purple-800 font-semibold">{call.score}</span>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <Badge variant="outline" className={call.reviewCompleted ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}>
                                {call.reviewCompleted ? "Completed" : "Declined"}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <Badge variant="outline" className={call.callStatus === 'Completed' ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}>
                                {call.callStatus === 'Completed' ? "Sent" : "Pending"}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedCall({
                                    id: call.id,
                                    patientName: call.patientName,
                                    phoneNumber: call.phone,
                                    locationId: call.locationId,
                                    location: call.locationName,
                                    lastVisitDate: new Date(call.lastVisitDate).toISOString().split('T')[0],
                                    callDate: call.callDate ? new Date(call.callDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                                    callStatus: call.callStatus,
                                    score: call.score,
                                    feedback: call.reviewContent || "",
                                    wouldRecommend: call.score >= 9,
                                    followUpRequested: false,
                                    needsAssistance: false,
                                    requestedReview: call.requestedReview,
                                    giftCardSent: call.callStatus === 'Completed',
                                    additionalComments: "",
                                    doctorCareRating: null,
                                    opticianCareRating: null,
                                    eyewearSatisfaction: null,
                                    glassesReceivedOnTime: null,
                                    contactsReceivedOnTime: null,
                                    appointmentEfficiency: null,
                                    officeCleanliness: null,
                                    feltWelcomed: null,
                                    wellInformed: null,
                                    improvementSuggestions: "",
                                    teamMemberToRecognize: ""
                                  });
                                  setDialogOpen(true);
                                }}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-3 text-center">
                          <div className="py-12">
                            <p className="text-center text-muted-foreground">No completed calls found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog for viewing call details */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Patient Survey Details</DialogTitle>
            <DialogDescription>
              Detailed information about the patient's survey call
            </DialogDescription>
          </DialogHeader>
          
          {selectedCall && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h3 className="font-semibold">Patient Information</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm font-medium">Name:</div>
                    <div className="text-sm">{selectedCall.patientName}</div>
                    
                    <div className="text-sm font-medium">Phone:</div>
                    <div className="text-sm">{selectedCall.phoneNumber}</div>
                    
                    <div className="text-sm font-medium">Location:</div>
                    <div className="text-sm">{selectedCall.location}</div>
                    
                    <div className="text-sm font-medium">Last Visit:</div>
                    <div className="text-sm">{selectedCall.lastVisitDate}</div>
                    
                    <div className="text-sm font-medium">Call Date:</div>
                    <div className="text-sm">{selectedCall.callDate}</div>
                    
                    <div className="text-sm font-medium">Call Status:</div>
                    <div className="text-sm">
                      <Badge className={
                        selectedCall.callStatus === "Completed" ? "bg-green-50 text-green-700" :
                        selectedCall.callStatus === "Declined" ? "bg-red-50 text-red-700" :
                        "bg-yellow-50 text-yellow-700"
                      }>
                        {selectedCall.callStatus}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="font-semibold">NPS Survey Result</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm font-medium">NPS Score:</div>
                    <div className="text-sm font-bold text-purple-800">{selectedCall.score}/10</div>
                    
                    <div className="text-sm font-medium">Would Recommend:</div>
                    <div className="text-sm">{selectedCall.wouldRecommend ? "Yes" : "No"}</div>
                    
                    <div className="text-sm font-medium">Google Review:</div>
                    <div className="text-sm">
                      <Badge variant="outline" className={selectedCall.requestedReview ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}>
                        {selectedCall.requestedReview ? "Completed" : "Declined"}
                      </Badge>
                    </div>
                    
                    <div className="text-sm font-medium">Gift Card:</div>
                    <div className="text-sm">
                      <Badge variant="outline" className={selectedCall.giftCardSent ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}>
                        {selectedCall.giftCardSent ? "Sent" : "Pending"}
                      </Badge>
                    </div>
                    
                    <div className="text-sm font-medium">Follow-up Requested:</div>
                    <div className="text-sm">{selectedCall.followUpRequested ? "Yes" : "No"}</div>
                    
                    <div className="text-sm font-medium">Needs Assistance:</div>
                    <div className="text-sm">{selectedCall.needsAssistance ? "Yes" : "No"}</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="font-semibold">Patient Feedback</h3>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-sm whitespace-pre-wrap">{selectedCall.feedback}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h3 className="font-semibold">Ratings</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm font-medium">Doctor Care:</div>
                    <div className="text-sm">{selectedCall.doctorCareRating || "N/A"}</div>
                    
                    <div className="text-sm font-medium">Optician Care:</div>
                    <div className="text-sm">{selectedCall.opticianCareRating || "N/A"}</div>
                    
                    <div className="text-sm font-medium">Eyewear Satisfaction:</div>
                    <div className="text-sm">{selectedCall.eyewearSatisfaction || "N/A"}</div>
                    
                    <div className="text-sm font-medium">Appointment Efficiency:</div>
                    <div className="text-sm">{selectedCall.appointmentEfficiency || "N/A"}</div>
                    
                    <div className="text-sm font-medium">Office Cleanliness:</div>
                    <div className="text-sm">{selectedCall.officeCleanliness || "N/A"}</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="font-semibold">Additional Feedback</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm font-medium">Glasses Received on Time:</div>
                    <div className="text-sm">{selectedCall.glassesReceivedOnTime === null ? "N/A" : selectedCall.glassesReceivedOnTime ? "Yes" : "No"}</div>
                    
                    <div className="text-sm font-medium">Contacts Received on Time:</div>
                    <div className="text-sm">{selectedCall.contactsReceivedOnTime === null ? "N/A" : selectedCall.contactsReceivedOnTime ? "Yes" : "No"}</div>
                    
                    <div className="text-sm font-medium">Felt Welcomed:</div>
                    <div className="text-sm">{selectedCall.feltWelcomed === null ? "N/A" : selectedCall.feltWelcomed ? "Yes" : "No"}</div>
                    
                    <div className="text-sm font-medium">Well Informed:</div>
                    <div className="text-sm">{selectedCall.wellInformed === null ? "N/A" : selectedCall.wellInformed ? "Yes" : "No"}</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="font-semibold">Improvement Suggestions</h3>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-sm whitespace-pre-wrap">{selectedCall.improvementSuggestions || "None provided"}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="font-semibold">Team Member to Recognize</h3>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-sm">{selectedCall.teamMemberToRecognize || "None specified"}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="font-semibold">Additional Comments</h3>
                <div className="bg-slate-50 p-3 rounded-md">
                  <p className="text-sm whitespace-pre-wrap">{selectedCall.additionalComments || "None"}</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}